import { type ReactNode, useMemo } from "react";
import type { FormErrors, TypedWrappedFieldProps } from "redux-form";
import { Field } from "redux-form";

import { Stack, Typography } from "@braintrust/braintrust-ui-components";
import { ErrorSVGIcon } from "@braintrust/braintrust-ui-components/Icons";
import type { TextFieldProps } from "@js/forms/fields/text";
import { TextField } from "@js/forms/fields/text";
import { useFormErrors } from "@js/forms/hooks/form-errors";
import { requiredWithSpecificMessage } from "@js/forms/validators";

import type { PublicFreelancerBidFormValues } from "../../apps/bids/utils/bid-form";
import { FieldSectionPanelBase } from "../../forms/fields/field-section-panel";

const requiredValidator = requiredWithSpecificMessage("This is required.");

export const PublicBidUserInfoSection = () => {
  return <Field name="_meta" component={PublicBidUserInfo} />;
};

const USER_INFO_FIELDS = {
  FIRST_NAME: "first_name",
  LAST_NAME: "last_name",
  EMAIL: "email",
  LINKEDIN_URL: "linkedin_url",
  WEBSITE_URL: "website_url",
} as const satisfies Record<string, keyof PublicFreelancerBidFormValues>;

const userInfoFields = Object.values(USER_INFO_FIELDS);

export const PublicBidUserInfo = ({ meta }: TypedWrappedFieldProps) => {
  const { errors } = useFormErrors(meta);
  const submitFailed = meta.submitFailed;
  const isError = useMemo(() => {
    const typedErrors = errors as FormErrors<PublicFreelancerBidFormValues>;
    return submitFailed && userInfoFields.some((field) => !!typedErrors[field]);
  }, [errors, submitFailed]);

  return (
    <FieldSectionPanelBase isError={isError} id="personal-info">
      <Stack sx={{ gap: 1 }}>
        <Field
          name={USER_INFO_FIELDS.FIRST_NAME}
          id={USER_INFO_FIELDS.FIRST_NAME}
          component={TextFieldWithCustomLabel}
          label="Legal first name"
          placeholder="Enter your first name"
          validate={[requiredValidator]}
          isRequired
        />
        <Field
          name={USER_INFO_FIELDS.LAST_NAME}
          id={USER_INFO_FIELDS.LAST_NAME}
          component={TextFieldWithCustomLabel}
          label="Legal last name"
          placeholder="Enter your last name"
          validate={[requiredValidator]}
          isRequired
        />
        <Field
          name={USER_INFO_FIELDS.EMAIL}
          id={USER_INFO_FIELDS.EMAIL}
          component={TextFieldWithCustomLabel}
          label="Email"
          placeholder="Enter your email"
          validate={[requiredValidator]}
          isRequired
        />
        <Field
          name={USER_INFO_FIELDS.LINKEDIN_URL}
          id={USER_INFO_FIELDS.LINKEDIN_URL}
          component={TextFieldWithCustomLabel}
          label="LinkedIn profile URL"
          placeholder="Enter your LinkedIn profile URL"
        />
        <Field
          name={USER_INFO_FIELDS.WEBSITE_URL}
          id={USER_INFO_FIELDS.WEBSITE_URL}
          component={TextFieldWithCustomLabel}
          label="Website"
          placeholder="Enter your website URL"
        />
      </Stack>
    </FieldSectionPanelBase>
  );
};

const CustomLabel = ({
  children,
  isRequired,
  isError,
}: {
  children: ReactNode;
  isRequired?: boolean;
  isError?: boolean;
}) => {
  return (
    <Stack direction="row" sx={{ alignItems: "center" }}>
      {isError && (
        <ErrorSVGIcon
          sx={{ color: "var(--negative-2)", fontSize: "16px", mr: 1 }}
        />
      )}
      <span>{children}</span>
      {!!isRequired && (
        <Typography
          color="tertiary"
          component="span"
          size="small"
          sx={{
            lineHeight: "1!important",
            display: "inline-block",
            ml: 0.75,
            mt: "1px",
          }}
        >
          Required
        </Typography>
      )}
    </Stack>
  );
};

const TextFieldWithCustomLabel = ({
  isRequired,
  ...props
}: TextFieldProps & { isRequired?: boolean }) => {
  const { meta } = props;
  const displayError = useMemo(() => !!meta.error && meta.submitFailed, [meta]);
  return (
    <TextField
      {...props}
      label={
        <CustomLabel isRequired={isRequired} isError={displayError}>
          {props.label}
        </CustomLabel>
      }
      showErrorWhen="submitFailed"
    />
  );
};
